import React, { useState } from 'react';
import { Star, Plus, Link as LinkIcon, Eye, Check, SealCheck, LinkedinLogo, FacebookLogo, Cigarette, CigaretteSlash, PawPrint, User, UsersThree, EnvelopeSimple, IdentificationCard, Coins, Wallet, MapPin, Flag, ArrowRight, HouseLine, PencilSimple } from '@phosphor-icons/react';
import { Skeleton } from '../components/ui/Skeleton';
import { Callout } from '../components/ui/Callout';
import { ActionMenu } from '../components/ui/ActionMenu';
import { EditProfileModal, EditProfileFocusSection, ProfileEditData } from '../components/forms/EditProfileModal';
import { VerifyEmailModal } from '../components/forms/VerifyEmailModal';
import { VerifyIdentityModal } from '../components/forms/VerifyIdentityModal';
import { VerifyIncomeModal } from '../components/forms/VerifyIncomeModal';
import { RequestReviewModal } from '../components/forms/RequestReviewModal';
import { EmailVerificationBanner } from '../components/forms/onboarding/EmailVerificationBanner';
import { OnboardingChecklist } from '../components/dashboard/OnboardingChecklist';
import { SupplyProfileView } from '../components/dashboard/SupplyProfileView';
import { ProfileCompletenessNudge } from '../components/dashboard/ProfileCompletenessNudge';
import { ReviewCard, Review, ReviewsRatingSummary } from '../components/dashboard/ReviewCard';
import { getDemoTenantReviewsForSlug } from './TenantProfile';
import { PendingReviewRequest, ReviewRequest } from '../components/dashboard/PendingReviewRequest';
import { IdentityCard } from '../components/dashboard/IdentityCard';
import { loadOnboardingDraft, getSession, cn, isFullyVerified, formatBudget, getAnuntHistory, getStoredReviewsForSubject } from '../lib/utils';
import { PAGE_ROUTE } from '../lib/routes';
// ─────────────────────────────────────────────────────────────────
// Pașaport — identity + reputation entity. Permanent, cumulative.
// Distinct from Anunț (the active rental search). See product taxonomy.
// ─────────────────────────────────────────────────────────────────
interface PasaportData {
  slug: string;
  firstName: string;
  lastName: string;
  avatar: string;
  occupation: string;
  city: string;
  verifications: {
    email: boolean;
    identity: boolean;
    income: boolean;
  };
  memberSince: string; // 'Mai 2026'
  memberSinceYear: number; // 2024
  rating: number;
  reviewCount: number;
  bio: string | null; // strict identity bio, NOT "ce caut"
  // Lifestyle — permanent traits, single source of truth on Pașaport
  livingMode: string; // 'Singur(ă)' / 'Cuplu' / 'Familie' / 'Co-living'
  livingModeValue: 'single' | 'couple' | 'family' | 'combo';
  isSmoker: boolean;
  hasPets: boolean;
  petTypes: string[];
  petOther: string;
  pets: string | null; // human-readable: '1 câine' | '2 pisici' | null
  // Social proof
  linkedinConnected: boolean;
  linkedinUrl: string | null;
  facebookConnected: boolean;
  facebookUrl: string | null;
  // Active anunț summary (denormalized — actual data lives on Anunț entity)
  activeAnunt: {
    hasActive: boolean;
    slug?: string;
    housingLabel?: string;
    rooms?: string;
    budgetMaxEur?: number;
    neighborhoods?: string;
    moveInDate?: string;
  };
}
const ANDREI_PASAPORT: PasaportData = {
  slug: 'andrei-popescu',
  firstName: 'Andrei',
  lastName: 'Popescu',
  avatar: 'https://i.pravatar.cc/150?img=11',
  occupation: 'Product Designer',
  city: 'București',
  verifications: {
    email: true,
    identity: false,
    income: false
  },
  memberSince: 'Ianuarie 2026',
  memberSinceYear: 2026,
  rating: 4.6,
  reviewCount: 0,
  bio: 'Sunt designer de produs, ordonat și liniștit. Lucrez de acasă 3 zile pe săptămână. Plătesc chiria la timp și am grijă de locuri unde stau.',
  livingMode: 'Singur(ă)',
  livingModeValue: 'single',
  isSmoker: false,
  hasPets: false,
  petTypes: [],
  petOther: '',
  pets: null,
  linkedinConnected: true,
  linkedinUrl: 'https://linkedin.com/in/andrei-popescu',
  facebookConnected: true,
  facebookUrl: 'https://facebook.com/andrei.popescu',
  activeAnunt: {
    hasActive: true,
    slug: 'andrei-popescu',
    housingLabel: 'Apartament',
    rooms: '2 cam',
    budgetMaxEur: 550,
    neighborhoods: 'Cotroceni, Militari',
    moveInDate: 'Iunie, 2026'
  }
};
const OLIVIA_PASAPORT: PasaportData = {
  slug: 'olivia-p',
  firstName: 'Olivia',
  lastName: 'P.',
  avatar: 'https://i.pravatar.cc/150?img=5',
  occupation: 'Freelancer',
  city: 'București',
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  memberSince: 'Februarie 2026',
  memberSinceYear: 2026,
  rating: 4.8,
  reviewCount: 2,
  bio: 'Bună, eu sunt Olivia. Lucrez de acasă ca freelancer și caut un apartament luminos, preferabil cu un spațiu pe care să-l pot amenaja ca birou. Suntem un cuplu liniștit și avem o pisică foarte educată.',
  livingMode: 'Cuplu',
  livingModeValue: 'couple',
  isSmoker: false,
  hasPets: true,
  petTypes: ['cat'],
  petOther: '',
  pets: '1 pisică',
  linkedinConnected: true,
  linkedinUrl: 'https://linkedin.com/in/olivia-p',
  facebookConnected: true,
  facebookUrl: 'https://facebook.com/olivia.p',
  activeAnunt: {
    hasActive: true,
    slug: 'olivia-p',
    housingLabel: 'Apartament',
    rooms: '3 cam',
    budgetMaxEur: 650,
    neighborhoods: 'Floreasca, Aviatorilor',
    moveInDate: 'Octombrie, 2026'
  }
};
const SARA_PASAPORT: PasaportData = {
  slug: 'combo-pair',
  firstName: 'Sara',
  lastName: 'V.',
  avatar: 'https://i.pravatar.cc/150?img=47',
  occupation: 'Designer',
  city: 'București',
  verifications: {
    email: true,
    identity: true,
    income: true
  },
  memberSince: 'Martie 2026',
  memberSinceYear: 2026,
  rating: 4.7,
  reviewCount: 0,
  bio: 'Sunt designer și caut un apartament împreună cu doi prieteni buni. Ne place liniștea, gătim acasă des și avem o pisică foarte educată. Plătim chiria împreună, contract pe numele meu.',
  livingMode: 'Co-living',
  livingModeValue: 'combo',
  isSmoker: false,
  hasPets: true,
  petTypes: ['cat'],
  petOther: '',
  pets: '1 pisică',
  linkedinConnected: true,
  linkedinUrl: 'https://linkedin.com/in/sara-v',
  facebookConnected: true,
  facebookUrl: 'https://facebook.com/sara.v',
  activeAnunt: {
    hasActive: true,
    slug: 'combo-pair',
    housingLabel: 'Apartament',
    rooms: '3 cam',
    budgetMaxEur: 900,
    neighborhoods: 'Cotroceni, Centru',
    moveInDate: 'Iulie, 2026'
  }
};
// Default fallback for any unknown / vizitator session so the original
// demo still works when no persona-matching email is present.
const MOCK_PASAPORT: PasaportData = ANDREI_PASAPORT;
function resolvePasaport(email?: string): PasaportData {
  switch (email) {
    case 'olivia.p@izio.test':
      return OLIVIA_PASAPORT;
    case 'sara.v@izio.test':
      return SARA_PASAPORT;
    case 'andrei.p@izio.test':
      return ANDREI_PASAPORT;
    default:
      return MOCK_PASAPORT;
  }
}
function computePasaportCompleteness(p: PasaportData) {
  // Completitudinea profilului e DECUPLATĂ de verificări.
  // Verificările sunt un trust layer separat (badge-uri pe profil).
  const checks = [{
    ok: !!p.avatar,
    label: 'fotografie'
  }, {
    ok: !!p.bio && p.bio.length >= 50,
    label: 'descriere'
  }, {
    ok: p.linkedinConnected || p.facebookConnected,
    label: 'cont social'
  }];
  const passed = checks.filter(c => c.ok).length;
  const percent = Math.round(passed / checks.length * 100);
  const missing = checks.filter(c => !c.ok).map(c => c.label);
  return {
    percent,
    missing
  };
}
interface DashboardProfilProps {
  navigate: (page: string, context?: any) => void;
}
export function DashboardProfil({
  navigate
}: DashboardProfilProps) {
  const session = getSession();
  const isSupply = session?.role === 'supply';
  const showChecklist = session?.role === 'demand' && session?.hasListing === false;
  const [pasaport, setPasaport] = useState<PasaportData | null>(showChecklist || isSupply ? null : resolvePasaport(session?.email));
  const [loading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editFocusSection, setEditFocusSection] = useState<EditProfileFocusSection | undefined>(undefined);
  const [verifyModal, setVerifyModal] = useState<'email' | 'identity' | 'income' | null>(null);
  const [incomePending, setIncomePending] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [reviewRequests, setReviewRequests] = useState<ReviewRequest[]>([]);
  // Demo-only: tenants see the same reviews on their dashboard that visitors
  // see on the public TenantProfile (seed + any stored submissions). Production
  // would unify this via a single user record.
  const [reviews, setReviews] = useState<Review[]>(() => {
    const seed = getDemoTenantReviewsForSlug(session?.slug);
    const stored = session?.slug ? getStoredReviewsForSubject(session.slug).map((r): Review => ({
      id: r.id,
      reviewerName: r.reviewerName,
      reviewerRole: r.reviewerRole as any,
      reviewerSlug: r.reviewerSlug,
      reviewerVerified: r.reviewerVerified,
      avatar: r.reviewerAvatar,
      rating: r.rating,
      categoryRatings: r.categoryRatings,
      date: r.date,
      body: r.body,
      periodLabel: '',
      status: r.status
    })) : [];
    return [...stored, ...seed];
  });
  const draft = loadOnboardingDraft();
  const needsVerification = draft?.account?.method === 'email' && draft?.emailVerified === false;
  if (isSupply && session) {
    return <SupplyProfileView session={session} navigate={navigate} data-id="element-3408" />;
  }
  if (showChecklist) {
    return <div className="space-y-6 max-w-2xl mx-auto" data-id="element-3409">
        {needsVerification && <EmailVerificationBanner email={draft.account.email} data-id="element-3410" />}
        <OnboardingChecklist navigate={navigate} userName={session?.name} data-id="element-3411" />
      </div>;
  }
  if (loading) {
    return <div className="space-y-6" data-id="element-3412">
        <Skeleton className="h-12 w-full rounded-xl" data-id="element-3413" />
        <Skeleton className="h-64 w-full rounded-2xl" data-id="element-3414" />
      </div>;
  }
  if (!pasaport) return null;
  const profileUrl = typeof window !== 'undefined' ? `${window.location.origin}/chirias/${pasaport.slug}` : `/chirias/${pasaport.slug}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  const viewAsPublic = () => navigate(PAGE_ROUTE.TENANT_PROFILE, {
    slug: pasaport.slug,
    viewerIsOwner: true
  });
  const openEdit = (section?: EditProfileFocusSection) => {
    setEditFocusSection(section);
    setEditOpen(true);
  };
  const closeEdit = () => {
    setEditOpen(false);
    setEditFocusSection(undefined);
  };
  const markVerified = (flag: 'email' | 'identity' | 'income') => {
    setPasaport(curr => curr ? {
      ...curr,
      verifications: {
        ...curr.verifications,
        [flag]: true
      }
    } : curr);
  };
  const handleEditSave = (next: ProfileEditData) => {
    setPasaport(curr => {
      if (!curr) return curr;
      const livingModeLabel = next.livingMode === 'single' ? 'Singur(ă)' : next.livingMode === 'couple' ? 'Cuplu' : next.livingMode === 'family' ? 'Familie' : 'Co-living';
      // Build human-readable pets summary from selected types
      const petLabels: Record<string, string> = {
        dog: 'câine',
        cat: 'pisică',
        other: next.petOther || 'animal'
      };
      const petsHuman = next.hasPets && next.petTypes.length > 0 ? next.petTypes.map(t => petLabels[t] || t).join(', ') : null;
      return {
        ...curr,
        avatar: next.avatar,
        firstName: next.firstName.trim(),
        lastName: next.lastName.trim(),
        occupation: next.occupation.trim(),
        bio: next.bio.trim() || null,
        isSmoker: next.isSmoker,
        hasPets: next.hasPets,
        petTypes: next.petTypes,
        petOther: next.petOther,
        pets: petsHuman,
        livingMode: livingModeLabel,
        livingModeValue: next.livingMode,
        linkedinUrl: next.linkedinUrl,
        linkedinConnected: !!next.linkedinUrl,
        facebookUrl: next.facebookUrl,
        facebookConnected: !!next.facebookUrl
      };
    });
    closeEdit();
  };
  const {
    percent,
    missing
  } = computePasaportCompleteness(pasaport);
  const fullyVerified = isFullyVerified(pasaport.verifications);
  return <div className="space-y-6" data-id="element-3415">
      {needsVerification && <div className="-mx-4 sm:-mx-8 -mt-8 mb-2" data-id="element-3416">
          <EmailVerificationBanner email={draft.account.email} data-id="element-3417" />
        </div>}

      {/* Info bar — completeness state */}
      {percent === 100 ? <Callout variant="success" dismissible dismissKey="profil_complete_100" data-id="element-3418">
          Profilul tău e 100% complet. Proprietarii pot vedea tot ce trebuie ca
          să-ți trimită oferte.
        </Callout> : <ProfileCompletenessNudge percent={percent} missingFields={missing} primaryLabel="Completează profilul" data-id="element-3419" />}

      {/* Page heading + ActionMenu */}
      <div className="flex items-center justify-between gap-3" data-id="element-3420">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3421">Profil</h1>
        <ActionMenu triggerAriaLabel="Acțiuni profil" items={[{
        label: 'Editează profil',
        icon: <PencilSimple size={18} weight="regular" data-id="element-3423" />,
        onClick: () => openEdit()
      }, {
        label: copied ? 'Link copiat' : 'Copiază link',
        icon: copied ? <Check size={18} weight="bold" data-id="element-3424" /> : <LinkIcon size={18} weight="regular" data-id="element-3425" />,
        onClick: copyLink,
        closeOnClick: false,
        rightSlot: copied ? <Check size={14} weight="bold" className="text-lime-1" data-id="element-3426" /> : undefined
      }, {
        label: 'Vezi cum apari',
        icon: <Eye size={18} weight="regular" data-id="element-3427" />,
        onClick: viewAsPublic
      }, {
        label: 'Verificări',
        icon: <SealCheck size={18} weight="regular" data-id="element-3428" />,
        onClick: () => {
          const el = document.getElementById('profile-verifications');
          if (el) {
            el.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }]} data-id="element-3422" />
      </div>

      {/* UNIFIED IDENTITY CARD */}
      <IdentityCard mode="owner" data={{
      firstName: pasaport.firstName,
      lastName: pasaport.lastName,
      avatar: pasaport.avatar,
      occupation: pasaport.occupation,
      bio: pasaport.bio,
      memberSinceYear: pasaport.memberSinceYear,
      rating: pasaport.rating,
      reviewCount: pasaport.reviewCount,
      verifications: pasaport.verifications,
      isSmoker: pasaport.isSmoker,
      hasPets: pasaport.hasPets,
      pets: pasaport.pets,
      livingMode: pasaport.livingMode,
      linkedinConnected: pasaport.linkedinConnected,
      linkedinUrl: pasaport.linkedinUrl,
      facebookConnected: pasaport.facebookConnected,
      facebookUrl: pasaport.facebookUrl,
      activeAnunt: pasaport.activeAnunt
    }} onOpenEditProfile={openEdit} onOpenVerification={type => setVerifyModal(type)} onNavigateToAnunt={() => {
      if (pasaport.activeAnunt.hasActive) {
        navigate(PAGE_ROUTE.DASHBOARD_ANUNT);
      } else {
        navigate(PAGE_ROUTE.ONBOARDING);
      }
    }} incomePending={incomePending} data-id="element-3429" />

      {/* REVIEWS CARD */}
      <div className="rounded-2xl bg-dark-2 border border-dark-3 px-6 py-7" data-id="element-3430">
        <div className="flex items-center justify-between mb-4" data-id="element-3431">
          <h3 className="text-base font-semibold text-light-1" data-id="element-3432">Recenzii</h3>
          <ReviewsRatingSummary reviews={reviews} data-id="element-3433" />
        </div>

        {reviews.length === 0 && reviewRequests.length === 0 ? <div className="rounded-xl border border-dashed border-dark-4 p-5 text-center" data-id="element-3434">
            <p className="text-sm text-light-3 leading-relaxed mb-3" data-id="element-3435">
              Nu ai recenzii încă. Cere una de la un fost proprietar — va apărea
              pe profil pentru totdeauna.
            </p>
            <button onClick={() => setRequestModalOpen(true)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium border border-dark-4 transition-colors cursor-pointer" data-id="element-3436">
              <Plus size={14} weight="bold" data-id="element-3437" />
              Cere o recenzie
            </button>
          </div> : <div className="space-y-4" data-id="element-3438">
            {reviews.map(r => <ReviewCard key={r.id} review={r} onReviewerClick={slug =>
        // Tenant dashboard: all reviewers are supply (proprietar/agent).
        navigate(PAGE_ROUTE.LANDLORD_PROFILE, {
          slug
        })} data-id="element-3439" />)}

            {reviewRequests.length > 0 && <div className="space-y-2" data-id="element-3440">
                <h4 className="text-xs font-medium text-light-3 uppercase tracking-wider mt-6 mb-3" data-id="element-3441">
                  Invitații trimise
                </h4>
                {reviewRequests.map(req => <PendingReviewRequest key={req.id} request={req} onResend={id => {
            setReviewRequests(prev => prev.map(r => r.id === id ? {
              ...r,
              sentAt: 'Acum'
            } : r));
          }} onCancel={id => {
            setReviewRequests(prev => prev.filter(r => r.id !== id));
          }} onTestLink={token => {
            navigate(PAGE_ROUTE.REVIEW_SUBMISSION, {
              token
            });
          }} data-id="element-3442" />)}
              </div>}

            <div className="pt-4" data-id="element-3443">
              <button onClick={() => setRequestModalOpen(true)} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-dark-3 hover:bg-dark-4 text-light-1 text-xs font-medium border border-dark-4 transition-colors cursor-pointer" data-id="element-3444">
                <Plus size={14} weight="bold" data-id="element-3445" />
                Cere altă recenzie
              </button>
            </div>
          </div>}
      </div>

      {/* RENTAL HISTORY CARD — populated from succes history entries */}
      {(() => {
      const succesEntries = getAnuntHistory().filter(e => e.status === 'succes');
      return <div className="rounded-2xl bg-dark-2 border border-dark-3 px-6 py-7" data-id="element-3446">
            <h3 className="text-base font-semibold text-light-1 mb-1" data-id="element-3447">
              Istoric închirieri
            </h3>
            <p className="text-xs text-light-3 mb-4" data-id="element-3448">
              Apartamentele unde ai stat — fiecare anunț marcat ca succes devine
              o experiență aici.
            </p>
            {succesEntries.length === 0 ? <p className="text-xs text-light-4" data-id="element-3449">
                Nu ai încă închirieri finalizate. Când marchezi o ofertă ca
                succes, va apărea aici.
              </p> : <ul className="space-y-2" data-id="element-3450">
                {succesEntries.map(entry => {
            const closedAt = entry.closedAt ? new Date(entry.closedAt).toLocaleDateString('ro-RO', {
              month: 'long',
              year: 'numeric'
            }) : '';
            return <li key={entry.id} className="flex items-center gap-3 p-3 rounded-xl bg-dark-1 border border-dark-3" data-id="element-3451">
                      <div className="w-9 h-9 rounded-lg bg-dark-3 inline-flex items-center justify-center shrink-0" data-id="element-3452">
                        <HouseLine size={16} weight="regular" className="text-light-2" data-id="element-3453" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-3454">
                        <p className="text-sm font-medium text-light-1 truncate" data-id="element-3455">
                          {entry.succesListingTitle || entry.anuntSnapshot.neighborhoods || 'Închiriere finalizată'}
                        </p>
                        <p className="text-xs text-light-3 mt-0.5 truncate" data-id="element-3456">
                          {entry.succesCounterpartyName ? `Cu ${entry.succesCounterpartyName}` : ''}
                          {closedAt && entry.succesCounterpartyName ? ' · ' : ''}
                          {closedAt ? `închis ${closedAt}` : ''}
                        </p>
                      </div>
                    </li>;
          })}
              </ul>}
          </div>;
    })()}

      {/* Edit profile modal */}
      <EditProfileModal open={editOpen} focusSection={editFocusSection} initialData={{
      avatar: pasaport.avatar,
      firstName: pasaport.firstName,
      lastName: pasaport.lastName,
      occupation: pasaport.occupation,
      linkedinUrl: pasaport.linkedinUrl,
      facebookUrl: pasaport.facebookUrl,
      bio: pasaport.bio || '',
      isSmoker: pasaport.isSmoker,
      hasPets: pasaport.hasPets,
      petTypes: pasaport.petTypes,
      petOther: pasaport.petOther,
      livingMode: pasaport.livingModeValue
    }} onClose={closeEdit} onSave={handleEditSave} data-id="element-3457" />

      {/* Verification modals */}
      <VerifyEmailModal open={verifyModal === 'email'} email={`${pasaport.firstName.toLowerCase()}.${pasaport.lastName.toLowerCase()}@example.com`} onClose={() => setVerifyModal(null)} onSuccess={() => {
      markVerified('email');
      setVerifyModal(null);
    }} data-id="element-3458" />
      <VerifyIdentityModal open={verifyModal === 'identity'} onClose={() => setVerifyModal(null)} onSuccess={() => {
      markVerified('identity');
      setVerifyModal(null);
    }} data-id="element-3459" />
      <VerifyIncomeModal open={verifyModal === 'income'} onClose={() => setVerifyModal(null)} onSuccess={() => {
      // Income verification is async — show "în verificare" until manual review.
      // For the demo, simulate the lime badge appearing after 4s.
      setIncomePending(true);
      setVerifyModal(null);
      setTimeout(() => {
        setIncomePending(false);
        markVerified('income');
      }, 4000);
    }} data-id="element-3460" />

      <RequestReviewModal open={requestModalOpen} navigate={navigate} onClose={() => setRequestModalOpen(false)} onSuccess={req => {
      setReviewRequests(prev => [req, ...prev]);
    }} data-id="element-3461" />
    </div>;
}